import { PosBffApi } from '@kinderlabs-pos/apis/pos';
import { OrderDetailType } from '@kinderlabs-pos/shared-data-type';
import { AdminStoreInfoState, PrintReceiptState, StoreInfoState } from '@kinderlabs-pos/state';
import { WithDialog, XlDialogProps } from '@kinderlabs-pos/ui-atoms';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputLabel,
	OutlinedInput,
	Stack,
	debounce,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

type OrderReceiptDialogProps = XlDialogProps & {
	orderWithDetail: OrderDetailType;
};
export const OrderReceiptDialog = ({
	open,
	closeDialog,
	orderWithDetail,
}: OrderReceiptDialogProps) => {
	const { handleClick주문영수증인쇄, handleClick환불영수증인쇄, handleClick간이영수증인쇄 } =
		use주문영수증수동인쇄({
			orderWithDetail,
		});

	const { isMobileReceiptAvailable } = useAtomValue(StoreInfoState.electronicReceiptConfig);
	const [electronicReceiptOpen, setElectronicReceiptOpen] = useState(false);
	const [phoneNumber, setPhoneNumber] = useState('');

	const handleClickElectronic영수증전송 = () => {
		setPhoneNumber(orderWithDetail.guestMemberInfo?.telephone ?? '');
		setElectronicReceiptOpen(true);
	};

	const handleConfirmElectronic영수증 = async () => {
		await PosBffApi.postElectronicReceipt({
			orderId: orderWithDetail.id!,
			phoneNumber,
		});
		setElectronicReceiptOpen(false);
		closeDialog && closeDialog();
	};

	return (
		<>
			<WithDialog.XlDialog
				dialogTitle={'주문 영수증 인쇄'}
				open={open}
				closeDialogAction='닫기'
				closeDialog={closeDialog}>
				<DialogContent>
					<Stack
						direction={'row'}
						spacing={1}>
						<Button
							variant='outlined'
							disabled={!orderWithDetail.completed}
							onClick={() => {
								handleClick주문영수증인쇄();
								closeDialog && closeDialog();
							}}>
							주문 영수증 재인쇄
						</Button>
						<Button
							variant='outlined'
							disabled={!orderWithDetail.canceled}
							onClick={() => {
								handleClick환불영수증인쇄();
								closeDialog && closeDialog();
							}}>
							환불 영수증 재인쇄
						</Button>
						<Button
							variant='outlined'
							disabled={!orderWithDetail.completed}
							onClick={() => {
								handleClick간이영수증인쇄();
								closeDialog && closeDialog();
							}}>
							간이 영수증 인쇄
						</Button>
						{isMobileReceiptAvailable && (
							<Button
								variant='outlined'
								disabled={!orderWithDetail.completed}
								onClick={handleClickElectronic영수증전송}>
								전자 영수증 전송
							</Button>
						)}
					</Stack>
				</DialogContent>
			</WithDialog.XlDialog>
			<Dialog
				open={electronicReceiptOpen}
				onClose={() => setElectronicReceiptOpen(false)}>
				<DialogTitle>전자 영수증 전송</DialogTitle>
				<DialogContent>
					<Stack
						spacing={1}
						mt={1}
						minWidth={280}>
						<InputLabel>전화번호</InputLabel>
						<OutlinedInput
							size={'small'}
							value={phoneNumber}
							onChange={(e) => setPhoneNumber(e.target.value)}
							placeholder={'01012345678'}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button
						variant='outlined'
						onClick={() => setElectronicReceiptOpen(false)}>
						닫기
					</Button>
					<Button
						variant='contained'
						disabled={!phoneNumber.trim()}
						onClick={handleConfirmElectronic영수증}>
						전송
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

const use주문영수증수동인쇄 = ({ orderWithDetail }: { orderWithDetail: OrderDetailType }) => {
	const { data } = useQuery({
		...AdminStoreInfoState.keys.all(),
		keepPreviousData: true,
		refetchOnWindowFocus: false,
	});
	const storeInfoList = data ?? [];
	const storeInfo = storeInfoList.find((storeInfo) => storeInfo.id === orderWithDetail.storeId);

	if (!storeInfo) throw Error('개발 에러 - storeInfo 검토');

	const 주문영수증재출력 = PrintReceiptState.use영수증수동출력({ receiptType: 'ORDER' });
	const 환불영수증재출력 = PrintReceiptState.use영수증수동출력({ receiptType: 'CANCEL' });
	const 간이영수증출력 = PrintReceiptState.use간이영수증출력();

	const handleClick주문영수증인쇄 = debounce(async () => {
		await 주문영수증재출력({
			order: orderWithDetail,
			payments: orderWithDetail.payments,
		});
	}, 100);

	const handleClick간이영수증인쇄 = debounce(async () => {
		await 간이영수증출력({
			order: orderWithDetail,
			payments: orderWithDetail.payments,
		});
	}, 100);

	const handleClick환불영수증인쇄 = debounce(async () => {
		await 환불영수증재출력({
			order: orderWithDetail,
			payments: orderWithDetail.payments,
		});
	}, 100);

	return {
		// 취소도 포함
		handleClick주문영수증인쇄,
		handleClick환불영수증인쇄,
		handleClick간이영수증인쇄,
	};
};
